import React, { useState } from "react";
import { toast } from "sonner";
import { Database, Download, Upload, Loader2, FileSpreadsheet } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import api from "@/lib/apiClient";

const ENTITIES = [
  { key: "batteries", label: "Batteries", importable: true },
  { key: "users", label: "Users & Partners", importable: true },
  { key: "claims", label: "Warranty Claims", importable: false },
];

export default function DataIO() {
  const [busy, setBusy] = useState("");

  const doExport = async (entity) => {
    setBusy(`export-${entity}`);
    try {
      const res = await api.get(`/export/${entity}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${entity}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${entity}.csv`);
    } catch { toast.error("Export failed."); }
    finally { setBusy(""); }
  };

  const doImport = async (entity, file) => {
    if (!file) return;
    setBusy(`import-${entity}`);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post(`/import/${entity}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${res.data.imported} ${entity} record(s).`);
    } catch (err) { toast.error(err.response?.data?.detail || "Import failed."); }
    finally { setBusy(""); }
  };

  return (
    <div className="space-y-6" data-testid="data-io-page">
      <div className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-lg bg-primary/10 flex items-center justify-center">
          <Database className="h-6 w-6 text-primary" />
        </div>
        <div>
          <h1 className="font-display text-3xl font-extrabold">Import / Export Data</h1>
          <p className="text-muted-foreground">Download records as CSV or bulk-import from a spreadsheet.</p>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        {ENTITIES.map((e) => (
          <Card key={e.key} className="p-6 flex flex-col" data-testid={`data-card-${e.key}`}>
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-accent flex items-center justify-center"><FileSpreadsheet className="h-5 w-5 text-accent-foreground" /></div>
              <h3 className="font-display font-bold text-lg">{e.label}</h3>
            </div>

            <div className="mt-5 space-y-3">
              <Button
                variant="outline"
                className="w-full gap-2"
                onClick={() => doExport(e.key)}
                disabled={busy === `export-${e.key}`}
                data-testid={`export-${e.key}`}
              >
                {busy === `export-${e.key}` ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                Export CSV
              </Button>

              {e.importable ? (
                <label className="w-full">
                  <div className={`w-full flex items-center justify-center gap-2 rounded-lg h-9 text-sm font-medium cursor-pointer bg-primary text-primary-foreground hover:brightness-105 transition ${busy === `import-${e.key}` ? "opacity-70 pointer-events-none" : ""}`} data-testid={`import-${e.key}`}>
                    {busy === `import-${e.key}` ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                    Import CSV
                  </div>
                  <input type="file" accept=".csv" className="hidden" data-testid={`import-input-${e.key}`}
                    onChange={(ev) => { doImport(e.key, ev.target.files?.[0]); ev.target.value = ""; }} />
                </label>
              ) : (
                <p className="text-xs text-muted-foreground text-center pt-1">Export only</p>
              )}
            </div>
          </Card>
        ))}
      </div>

      <Card className="p-5 bg-accent/40">
        <p className="eyebrow mb-2">Import tips</p>
        <ul className="text-sm text-muted-foreground list-disc pl-5 space-y-1">
          <li>Use the exported CSV as a template — keep the header row unchanged.</li>
          <li><strong>Batteries</strong> match on <code>serial_number</code>; existing serials are updated, new ones created.</li>
          <li><strong>Users</strong> match on <code>email</code>; role must be one of the portal roles (defaults to customer).</li>
        </ul>
      </Card>
    </div>
  );
}
